import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, FileCheck2, RefreshCw, ShieldPlus } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Chip, PageHeader, ReasonsList, RestrictedState, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { POL6_STATUS, POL6_GAP_SEV, POL6_GAP_STATUS, POL6_REN_STATUS, fmtDateTime } from "../lib/labels";

const TABS = [["cuadro", "Cuadro de mando"], ["polizas", "Pólizas"], ["gaps", "Gaps de cobertura"], ["renovaciones", "Renovaciones"], ["requisitos", "Requisitos"]];
const POL_ACTIONS = {
  extraer: "Extraer datos (IA mock)", proponer_validacion: "Proponer validación", validar: "Validar extracción",
  solicitar_aprobacion: "Solicitar aprobación", aprobar: "Aprobar (entra en vigor)", rechazar_aprobacion: "Rechazar",
  iniciar_renovacion: "Iniciar renovación", marcar_caducada: "Marcar caducada", cancelar: "Cancelar", bloquear: "Bloquear", desbloquear: "Desbloquear",
};
const BY_STATUS = {
  recibida: ["extraer"], en_extraccion: ["proponer_validacion"], pendiente_validacion: ["validar"],
  en_revision: ["solicitar_aprobacion"], pendiente_aprobacion: ["aprobar", "rechazar_aprobacion"],
  vigente: ["iniciar_renovacion", "bloquear"], proxima_vencer: ["iniciar_renovacion", "bloquear"],
  en_renovacion: ["bloquear"], caducada: [], bloqueada: ["desbloquear"],
};

export default function PolizasCorp() {
  const { user } = useAuth();
  const { map, users } = useUsers();
  const qc = useQueryClient();
  const [tab, setTab] = useState("cuadro");
  const [sel, setSel] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [f, setF] = useState({});
  const [err, setErr] = useState(null);

  const refresh = () => qc.invalidateQueries();
  const { data: cuadro, error: cuadroErr } = useQuery({ queryKey: ["pol-cuadro"], queryFn: async () => (await api.get("/polizas/cuadro")).data });
  const { data: polizas } = useQuery({ queryKey: ["pol-list"], queryFn: async () => (await api.get("/polizas")).data });
  const { data: gaps } = useQuery({ queryKey: ["pol-gaps"], queryFn: async () => (await api.get("/polizas/gaps")).data });
  const { data: rens } = useQuery({ queryKey: ["pol-rens"], queryFn: async () => (await api.get("/polizas/renovaciones")).data });
  const { data: reqs } = useQuery({ queryKey: ["pol-reqs"], queryFn: async () => (await api.get("/polizas/requisitos")).data });
  const { data: det } = useQuery({ queryKey: ["pol-det", sel], queryFn: async () => (await api.get(`/polizas/${sel}`)).data, enabled: !!sel });

  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: () => { toast.success("Operación aplicada"); setDlg(null); setF({}); setErr(null); refresh(); },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error"); },
  });
  const open = (d) => { setDlg(d); setF({}); setErr(null); };
  const act = (pid, action, extra = {}) => mut.mutate({ path: `/polizas/${pid}/accion`, body: { action, ...extra } });

  if (user?.is_contractor || cuadroErr?.response?.status === 403) {
    return (
      <div className="space-y-4" data-testid="polizas-page">
        <PageHeader title="Pólizas, certificados y renovaciones" subtitle="" testid="polizas-title" />
        <RestrictedState module="las pólizas y seguros" detail={cuadroErr?.response?.data?.detail} testid="polizas-restricted" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="polizas-page">
      <PageHeader title="Pólizas, certificados y renovaciones"
        subtitle="Extracción gobernada (la IA propone y la persona valida), gaps deterministas, endorsements que no sobrescriben y renovaciones con comparación."
        testid="polizas-title"
        actions={<>
          <Button variant="outline" onClick={() => mut.mutate({ path: "/polizas/barrido" })} data-testid="pol-barrido-btn"><RefreshCw className="mr-1 h-4 w-4" /> Barrido de vencimientos</Button>
          <Button onClick={() => open({ kind: "nueva" })} data-testid="pol-new-btn"><ShieldPlus className="mr-1 h-4 w-4" /> Nueva póliza</Button>
        </>} />
      <div className="flex flex-wrap gap-1">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${tab === k ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
        ))}
      </div>

      {tab === "cuadro" && cuadro && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6" data-testid="pol-cuadro">
          {Object.entries(cuadro).map(([k, v]) => (
            <button key={k} className="ts-card ts-card-hover p-3 text-left" data-testid={`kpi-${k}`}
              onClick={() => setTab(k.startsWith("gaps") || k === "certificados_ausentes" || k === "procesos_bloqueados" ? "gaps" : k === "en_renovacion" ? "renovaciones" : "polizas")}>
              <p className="ts-caption">{k.replaceAll("_", " ")}</p>
              <p className={`font-display text-2xl font-bold ${v ? (k.includes("critico") || k.includes("bloqueado") || k === "caducadas" ? "text-red-700" : "text-slate-900") : "text-slate-500"}`}>{v}</p>
            </button>
          ))}
        </div>
      )}

      {tab === "polizas" && (
        <div className="space-y-2" data-testid="pol-list">
          {(polizas || []).map((p) => (
            <div key={p.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`pol-${p.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <button className="text-sm font-semibold text-blue-800 hover:underline" onClick={() => setSel(sel === p.id ? null : p.id)} data-testid={`pol-open-${p.id}`}>{p.title}</button>
                <Chip map={POL6_STATUS} value={p.status} testid={`pol-status-${p.id}`} />
                {p.status === "caducada" && <ToneChip label="revisión de impactos crítica" tone="danger" />}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {p.policy_number} · {p.insurer} · vigencia {String(p.valid_from).slice(0, 10)} → {String(p.valid_until).slice(0, 10)} ·
                coberturas: {(p.coverages || []).map((c) => `${c.type} ${(c.limit || 0).toLocaleString("es-ES")} €`).join(", ") || "—"} · propietario {userName(map, p.owner_id)}
              </p>
              <div className="mt-2 flex flex-wrap gap-1">
                {(BY_STATUS[p.status] || []).map((a) => (
                  <Button key={a} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`pol-${a}-${p.id}`}
                    onClick={() => ["solicitar_aprobacion", "rechazar_aprobacion", "cancelar", "bloquear", "desbloquear"].includes(a) ? open({ kind: "accion", pol: p, action: a }) : act(p.id, a)}>
                    {POL_ACTIONS[a]}
                  </Button>
                ))}
                {["vigente", "proxima_vencer", "en_renovacion"].includes(p.status) && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`pol-end-${p.id}`} onClick={() => open({ kind: "endorsement", pol: p })}>Añadir endorsement</Button>
                )}
              </div>
              {sel === p.id && det && (
                <div className="mt-3 space-y-2 rounded border border-slate-100 bg-slate-50 p-3" data-testid={`pol-det-${p.id}`}>
                  {det.policy.extraction && (
                    <div>
                      <h4 className="text-xs font-bold uppercase text-slate-500">Extracción (IA mock, validación humana)</h4>
                      <div className="mt-1 grid gap-1 md:grid-cols-2">
                        {Object.entries(det.policy.extraction).map(([k, v]) => (
                          <p key={k} className="text-xs" data-testid={`ext-${p.id}-${k}`}>
                            <strong>{k}:</strong> {String(v.value)} · pág. {v.page} · conf. {Math.round((v.confidence || 0) * 100)}% ·
                            {v.validated_by ? <span className="text-emerald-700"> validado por {userName(map, v.validated_by)}</span> : <span className="text-amber-700"> pendiente de validación</span>}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {det.endorsements.length > 0 && (
                    <div><h4 className="text-xs font-bold uppercase text-slate-500">Endorsements</h4>
                      {det.endorsements.map((e) => (
                        <div key={e.id} className="mt-1 flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white p-2 text-xs" data-testid={`end-${e.id}`}>
                          <span className="font-medium">{e.title}</span><ToneChip label={e.status} tone={e.status === "pendiente" ? "warning" : "success"} />
                          <span className="text-slate-500">modifica: {e.modifies}</span>
                          {e.status === "pendiente" && <Button size="sm" className="h-6 px-2 text-xs" data-testid={`end-accept-${e.id}`} onClick={() => mut.mutate({ path: `/polizas/endorsements/${e.id}/aceptar` })}>Aceptar y recalcular gaps</Button>}
                        </div>
                      ))}
                    </div>
                  )}
                  {det.gaps.length > 0 && (
                    <div><h4 className="text-xs font-bold uppercase text-slate-500">Gaps</h4>
                      {det.gaps.map((g) => (
                        <p key={g.id} className="text-xs" data-testid={`pol-gap-${g.id}`}>
                          <Chip map={POL6_GAP_SEV} value={g.severity} /> <Chip map={POL6_GAP_STATUS} value={g.status} /> {g.difference}
                          {g.blocked_process ? ` · bloquea: ${g.blocked_process}` : ""}
                        </p>
                      ))}
                    </div>
                  )}
                  {det.renewals.length > 0 && <p className="text-xs text-slate-500">Renovaciones: {det.renewals.map((r) => `${r.id} (${r.status}, trigger ${r.trigger})`).join(" · ")}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "gaps" && (
        <div className="space-y-2" data-testid="pol-gaps">
          {(gaps || []).map((g) => (
            <div key={g.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`gap-${g.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip map={POL6_GAP_SEV} value={g.severity} testid={`gap-sev-${g.id}`} />
                <Chip map={POL6_GAP_STATUS} value={g.status} testid={`gap-status-${g.id}`} />
                <span className="text-xs font-medium">{g.kind.replaceAll("_", " ")}</span>
              </div>
              <p className="mt-1 text-xs text-slate-600">{g.difference}</p>
              <p className="text-xs text-slate-400">requisito {g.requirement_id} · póliza {g.policy_id} · fuente {g.source}
                {g.blocked_process ? ` · BLOQUEA: ${g.blocked_process}` : ""} · responsable {userName(map, g.owner_id)}</p>
              <p className="text-xs text-slate-500">Acción necesaria: {g.action_needed}</p>
              {["abierto", "en_tratamiento"].includes(g.status) && (
                <Button size="sm" variant="outline" className="mt-1 h-6 px-2 text-xs" data-testid={`gap-resolve-${g.id}`} onClick={() => open({ kind: "gap", gap: g })}>Resolver / aceptar riesgo</Button>
              )}
              {g.status === "resuelto" && <p className="mt-1 text-xs text-emerald-700">Resuelto {g.resolved_at?.slice(0, 10)} {g.evidence_id ? `· evidencia ${g.evidence_id}` : ""}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === "renovaciones" && (
        <div className="space-y-2" data-testid="pol-rens">
          {(rens || []).map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`ren-${r.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold">Renovación de {r.policy_id}</span>
                <Chip map={POL6_REN_STATUS} value={r.status} testid={`ren-status-${r.id}`} />
                <ToneChip label={`trigger: ${r.trigger}`} tone="info" />
              </div>
              <ul className="mt-1 space-y-0.5">
                {(r.checklist || []).map((c, i) => (
                  <li key={i} className="text-xs">{c.done ? "☑" : "☐"} {c.item}</li>
                ))}
              </ul>
              {r.comparison && <p className="mt-1 text-xs text-slate-500">Comparación: límite {r.comparison.old.limit?.toLocaleString("es-ES")} € → {r.comparison.new.limit?.toLocaleString("es-ES")} €</p>}
              {!["completada", "no_renovada"].includes(r.status) && (
                <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" data-testid={`ren-complete-${r.id}`} onClick={() => open({ kind: "ren", ren: r })}>Completar renovación</Button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "requisitos" && (
        <div className="space-y-2" data-testid="pol-reqs">
          {(reqs || []).map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 bg-white p-3 text-xs" data-testid={`req-${r.id}`}>
              <p className="font-medium">{r.coverage_type} · límite requerido {(r.required_limit || 0).toLocaleString("es-ES")} €{r.requires_certificate ? " · exige certificado" : ""}{r.blocking_rule ? " · REGLA DE BLOQUEO" : ""}</p>
              <p className="text-slate-500">fuente: {r.source} ({r.source_ref || "—"}) · entidad: {r.affected_entity || "todas"}{r.blocked_process ? ` · bloquea: ${r.blocked_process}` : ""}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent data-testid="pol-dialog">
          <DialogHeader><DialogTitle>
            {dlg?.kind === "nueva" && "Nueva póliza (estado Recibida)"}
            {dlg?.kind === "accion" && `${POL_ACTIONS[dlg.action]} — ${dlg.pol.title}`}
            {dlg?.kind === "endorsement" && `Endorsement — ${dlg.pol.title}`}
            {dlg?.kind === "gap" && `Resolver gap ${dlg.gap.kind}`}
            {dlg?.kind === "ren" && `Completar renovación ${dlg.ren.id}`}
          </DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              {dlg.kind === "nueva" && (
                <>
                  <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="f-title" /></div>
                  <div><Label>Aseguradora *</Label><Input value={f.insurer || ""} onChange={(e) => setF({ ...f, insurer: e.target.value })} data-testid="f-insurer" /></div>
                  <div><Label>Tomador *</Label><Input value={f.policyholder || ""} onChange={(e) => setF({ ...f, policyholder: e.target.value })} data-testid="f-holder" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Inicio vigencia</Label><Input type="date" value={f.valid_from || ""} onChange={(e) => setF({ ...f, valid_from: e.target.value })} data-testid="f-from" /></div>
                    <div><Label>Fin vigencia</Label><Input type="date" value={f.valid_until || ""} onChange={(e) => setF({ ...f, valid_until: e.target.value })} data-testid="f-until" /></div>
                  </div>
                  <div><Label>Límite RC general (€)</Label><Input type="number" value={f.limit || ""} onChange={(e) => setF({ ...f, limit: Number(e.target.value) })} data-testid="f-limit" /></div>
                </>
              )}
              {dlg.kind === "accion" && dlg.action === "solicitar_aprobacion" && (
                <div><Label>Aprobador *</Label>
                  <Select value={f.approver_id || ""} onValueChange={(v) => setF({ ...f, approver_id: v })}>
                    <SelectTrigger data-testid="f-approver"><SelectValue placeholder="Aprobador" /></SelectTrigger>
                    <SelectContent>{users.filter((u) => (u.roles || []).some((r) => ["APPROVER", "EXECUTIVE", "DEMO_ADMIN"].includes(r))).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dlg.kind === "accion" && ["rechazar_aprobacion", "cancelar", "bloquear", "desbloquear"].includes(dlg.action) && (
                <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
              )}
              {dlg.kind === "endorsement" && (
                <>
                  <p className="text-xs text-slate-500">El endorsement crea una versión nueva; la póliza anterior se conserva. Al aceptarlo se recalculan los gaps.</p>
                  <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="f-title" /></div>
                  <div><Label>Qué modifica *</Label><Input value={f.modifies || ""} onChange={(e) => setF({ ...f, modifies: e.target.value })} data-testid="f-modifies" placeholder="límite rc_general" /></div>
                  <div><Label>Nuevo límite rc_general (€, opcional)</Label><Input type="number" value={f.limit || ""} onChange={(e) => setF({ ...f, limit: Number(e.target.value) })} data-testid="f-limit" /></div>
                </>
              )}
              {dlg.kind === "gap" && (
                <>
                  <p className="text-xs text-slate-500">{dlg.gap.difference}. Resolver exige evidencia; aceptar el riesgo exige motivo.</p>
                  <div><Label>Acción</Label>
                    <Select value={f.action || "resolver"} onValueChange={(v) => setF({ ...f, action: v })}>
                      <SelectTrigger data-testid="f-gaction"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="resolver">Resolver con evidencia</SelectItem><SelectItem value="aceptar_riesgo">Aceptar riesgo (con motivo)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {(f.action || "resolver") === "resolver"
                    ? <div><Label>ID de evidencia *</Label><Input value={f.evidence_id || ""} onChange={(e) => setF({ ...f, evidence_id: e.target.value })} data-testid="f-evidence" placeholder="evd_… / end_…" /></div>
                    : <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>}
                </>
              )}
              {dlg.kind === "ren" && (
                <>
                  <div><Label>Decisión</Label>
                    <Select value={f.decision || "renovar"} onValueChange={(v) => setF({ ...f, decision: v })}>
                      <SelectTrigger data-testid="f-decision"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="renovar">Sustituir por nueva póliza vigente</SelectItem><SelectItem value="no_renovar">No renovar (requiere aprobación)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {(f.decision || "renovar") === "renovar"
                    ? <div><Label>ID de la nueva póliza vigente *</Label><Input value={f.new_policy_id || ""} onChange={(e) => setF({ ...f, new_policy_id: e.target.value })} data-testid="f-newpol" placeholder="pol_…" /></div>
                    : <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.approved} onChange={(e) => setF({ ...f, approved: e.target.checked })} data-testid="f-approved" /> La decisión de no renovar está aprobada</label>}
                </>
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="pol-error">{apiError(err)}</p>)}
              <Button data-testid="pol-dialog-submit" disabled={mut.isPending}
                onClick={() => {
                  if (dlg.kind === "nueva") mut.mutate({ path: "/polizas", body: { title: f.title, insurer: f.insurer, policyholder: f.policyholder, valid_from: f.valid_from, valid_until: f.valid_until, coverages: f.limit ? [{ type: "rc_general", limit: f.limit }] : [] } });
                  else if (dlg.kind === "accion") act(dlg.pol.id, dlg.action, f);
                  else if (dlg.kind === "endorsement") mut.mutate({ path: `/polizas/${dlg.pol.id}/endorsements`, body: { title: f.title, modifies: f.modifies, coverage_update: f.limit ? { type: "rc_general", limit: f.limit } : undefined } });
                  else if (dlg.kind === "gap") mut.mutate({ path: `/polizas/gaps/${dlg.gap.id}/resolver`, body: f });
                  else if (dlg.kind === "ren") mut.mutate({ path: `/polizas/renovaciones/${dlg.ren.id}/completar`, body: f.decision === "no_renovar" ? { decision: "no_renovar", approved: !!f.approved } : { new_policy_id: f.new_policy_id } });
                }}>Confirmar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
